import React, { useState, useEffect, useCallback } from "react";
import {
  FaSearch,
  FaFacebook,
  FaInstagram,
  FaFacebookMessenger,
  FaExternalLinkAlt,
  FaGlobe,
  FaFilter,
  FaCheckCircle,
} from "react-icons/fa";
import { LuChevronDown } from "react-icons/lu";
import axios from "../lib/axios";
import "../CSS/dm-editorial.css";

const countryOptions = [
  { value: "IN", label: "India (IN)" },
  { value: "US", label: "United States (US)" },
  { value: "GB", label: "United Kingdom (GB)" },
  { value: "CA", label: "Canada (CA)" },
  { value: "ALL", label: "All Countries" },
];

const categoryOptions = [
  { value: "ALL", label: "All Ad Categories" },
  { value: "POLITICAL_AND_ISSUE_ADS", label: "Issues, Elections or Politics" },
  { value: "HOUSING", label: "Housing Ads" },
  { value: "EMPLOYMENT", label: "Employment Ads" },
  { value: "CREDIT", label: "Credit Ads" },
];

const statusOptions = [
  { value: "ACTIVE", label: "Active Ads" },
  { value: "INACTIVE", label: "Inactive Ads" },
  { value: "ALL", label: "All Statuses" },
];

export const MetaAdLibrarySection = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [country, setCountry] = useState("IN");
  const [category, setCategory] = useState("ALL");
  const [status, setStatus] = useState("ACTIVE");
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debounce search input by 400ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchAds = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (debouncedSearchTerm) params.append("search_terms", debouncedSearchTerm);
      if (country) params.append("country", country);
      if (category) params.append("ad_type", category);
      if (status) params.append("active_status", status);
      params.append("limit", "12");

      const response = await axios.get(`/api/facebook-ad-library?${params.toString()}`);
      const docs = response.data?.docs || [];
      setAds(docs);
    } catch (err) {
      console.error("Error fetching Meta Ad Library ads:", err);
      setError("Failed to load Ad Library results");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, country, category, status]);

  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchAds();
  };

  const getPlatformIcon = (platform) => {
    switch (String(platform).toLowerCase()) {
      case "facebook":
        return <FaFacebook key={platform} className="text-[#1877f2] text-sm" />;
      case "instagram":
        return <FaInstagram key={platform} className="text-[#e4405f] text-sm" />;
      case "messenger":
        return <FaFacebookMessenger key={platform} className="text-[#0084ff] text-sm" />;
      default:
        return <FaGlobe key={platform} className="text-[#161310]/50 text-xs" />;
    }
  };

  return (
    <section className="border-y border-[#161310]/10 bg-[#ece1d3] py-8 sm:py-10">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#161310] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#ffb2c0] mb-3">
            <FaFacebook className="text-sm text-white" />
            Meta Ad Library Explorer
          </div>
          <h2 className="dm-serif text-3xl sm:text-4xl font-light text-[#161310] tracking-tight">
            Search active digital &amp; Meta ads
          </h2>
          <p className="mt-2.5 text-sm sm:text-base text-[#161310]/60 max-w-2xl mx-auto">
            Discover real-time active advertisements running across Facebook,
            Instagram, and Meta platforms — with full transparency.
          </p>
        </div>

        {/* Filter & Search Bar Controls — sticky while browsing this section */}
        <div className="sticky top-[176px] md:top-[224px] lg:top-[132px] z-30 mb-6 rounded-[1.75rem] border border-[#161310] bg-[#161310] p-4 sm:p-5 space-y-4 shadow-[0_18px_40px_-20px_rgba(22,19,16,0.6)]">
          <form onSubmit={handleSearchSubmit} className="flex flex-col md:flex-row gap-3">
            {/* Location Selector */}
            <div className="flex items-center gap-2 rounded-xl border border-white bg-white px-3 py-2 text-xs text-[#161310] min-w-[150px] shadow-sm">
              <FaGlobe className="text-[#161310]/50 text-xs shrink-0" />
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full bg-white outline-none text-xs text-[#161310] cursor-pointer"
              >
                {countryOptions.map((opt) => (
                  <option key={opt.value} value={opt.value} className="bg-white text-[#161310]">
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Ad Category Selector */}
            <div className="flex items-center gap-2 rounded-xl border border-white bg-white px-3 py-2 text-xs text-[#161310] min-w-[180px] shadow-sm">
              <FaFilter className="text-[#161310]/50 text-xs shrink-0" />
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-white outline-none text-xs text-[#161310] cursor-pointer"
              >
                {categoryOptions.map((opt) => (
                  <option key={opt.value} value={opt.value} className="bg-white text-[#161310]">
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Active Status Selector */}
            <div className="flex items-center gap-2 rounded-xl border border-white bg-white px-3 py-2 text-xs text-[#161310] min-w-[140px] shadow-sm">
              <FaCheckCircle className="text-emerald-600 text-xs shrink-0" />
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full bg-white outline-none text-xs text-[#161310] cursor-pointer"
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value} className="bg-white text-[#161310]">
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Search Input */}
            <div className="relative flex-1">
              <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#161310]/50 text-xs" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search ads by keyword, brand, or advertiser..."
                className="w-full rounded-xl border border-white bg-white py-2 pl-9 pr-24 text-xs font-medium text-[#161310] outline-none transition placeholder:text-[#161310]/40 focus:border-[#bc1535] focus:ring-2 focus:ring-[#bc1535]/25 shadow-sm"
              />
              <button
                type="submit"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-lg bg-[#bc1535] px-3.5 py-1 text-xs font-semibold text-white transition hover:bg-[#a10f2b] shadow-sm"
              >
                Search
              </button>
            </div>
          </form>
        </div>

        {/* Results Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="w-10 h-10 border-4 border-[#bc1535] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-[#a10f2b] text-sm">{error}</div>
        ) : ads.length === 0 ? (
          <div className="text-center py-12 rounded-[1.75rem] bg-[#fcfaf7] border border-[#161310]/10 p-8">
            <FaSearch className="mx-auto text-3xl text-[#161310]/20 mb-3" />
            <p className="text-[#161310] font-semibold text-base">No active ads found</p>
            <p className="text-[#161310]/50 text-xs mt-1">
              Try adjusting your search terms or filters above.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {ads.map((ad) => {
              const bodyText = ad.ad_creative_bodies?.[0] || ad.body || "";
              const titleText = ad.ad_creative_link_titles?.[0] || ad.title || "";
              const captionText = ad.ad_creative_link_captions?.[0] || ad.caption || "";
              const mediaUrl = ad.media_url || ad.image_url;

              return (
                <div
                  key={ad.id}
                  className="flex flex-col justify-between rounded-[1.5rem] bg-[#fcfaf7] border border-[#161310]/10 hover:border-[#161310]/25 hover:shadow-[0_24px_48px_-30px_rgba(22,19,16,0.45)] transition duration-200 overflow-hidden"
                >
                  {/* Card Header */}
                  <div className="p-4 border-b border-[#161310]/8 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <img
                          src={ad.page_profile_picture || "/assets/topup.png"}
                          alt={ad.page_name}
                          className="w-9 h-9 rounded-full object-cover border border-[#161310]/10 shrink-0"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://ui-avatars.com/api/?name=" + encodeURIComponent(ad.page_name || "Ad");
                          }}
                        />
                        <div className="min-w-0">
                          <h4 className="text-sm font-bold text-[#161310] truncate leading-tight">
                            {ad.page_name}
                          </h4>
                          <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded inline-block">
                            ● Active
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {Array.isArray(ad.publisher_platforms) &&
                          ad.publisher_platforms.map(getPlatformIcon)}
                      </div>
                    </div>

                    <div className="text-[11px] text-[#161310]/40 font-medium">
                      ID: {ad.id} • Started {ad.ad_delivery_start_time || "Recently"}
                    </div>
                  </div>

                  {/* Card Media Preview */}
                  {mediaUrl && (
                    <div className="relative aspect-video w-full bg-[#ece1d3] overflow-hidden">
                      <img
                        src={mediaUrl}
                        alt="Ad preview"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}

                  {/* Card Copy & Title */}
                  <div className="p-4 flex-1 space-y-2">
                    {bodyText && (
                      <p className="text-xs text-[#161310]/70 leading-relaxed line-clamp-3">
                        {bodyText}
                      </p>
                    )}

                    {titleText && (
                      <div className="pt-2 border-t border-[#161310]/5">
                        <h5 className="text-xs font-bold text-[#161310] line-clamp-2">
                          {titleText}
                        </h5>
                        {captionText && (
                          <span className="text-[10px] text-[#161310]/40 mt-0.5 block truncate">
                            {captionText}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Card Footer Action */}
                  <div className="p-3 bg-[#f5efe8]/60 border-t border-[#161310]/10 flex items-center justify-between">
                    <span className="text-[10px] font-medium text-[#161310]/45">
                      Meta Ad Library
                    </span>
                    <a
                      href={ad.snapshot_url || `https://www.facebook.com/ads/library/?id=${ad.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-bold text-[#bc1535] hover:text-[#a10f2b] transition"
                    >
                      See Ad Details
                      <FaExternalLinkAlt className="text-[9px]" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default MetaAdLibrarySection;
